import React, { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';
import { Link, useLocation } from 'react-router-dom';
import { loadPostHog, stopPostHog } from '../utils/analytics/posthogLoader';
import { loadSentry, stopSentry } from '../utils/analytics/sentryLoader';

const CONSENT_KEY = 'myinvestfolio_cookie_consent';
// Bump when the disclosed services change. Banner re-prompts users whose
// stored consent is older than the current version.
const CONSENT_VERSION = 2;

// Routes where the banner is intentionally hidden so it can't overlap form
// inputs (T&C checkbox, password field, etc.). PostHog stays default-denied
// while the banner is hidden — no tracking happens, consent is just deferred
// to the next page after auth.
const HIDE_ON_PATHS = ['/login', '/register'];

/**
 * Apply (or revoke) all third-party trackers based on a consent decision.
 * Centralised here so the banner, the "Cookie preferences" link, and any
 * future opt-out flow share the same behaviour. Symmetric for both trackers:
 *   accepted   → loadPostHog + loadSentry
 *   rejected   → stopPostHog + stopSentry
 *   undecided  → (banner mount stops both, see useEffect below)
 */
export function applyConsent(accepted) {
  if (accepted) {
    loadPostHog();
    loadSentry();
    return;
  }
  stopPostHog();
  stopSentry();
}

const writeConsent = (accepted) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({
    accepted,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  }));
  // Same-tab notification so the CookiePreferencesPanel (which now lives on
  // the same page as the banner, under Your Privacy) can refresh instantly.
  // The browser's 'storage' event does not fire in the tab that wrote the
  // value, so we emit our own.
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
};

const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) { logger.error(err); return null; }
};

// Tiny event bus so SettingsTab can pop the banner back open when the user
// clicks "Cookie preferences".
const REOPEN_EVENT = 'mif:reopen-cookie-banner';
export const reopenCookieBanner = () => window.dispatchEvent(new Event(REOPEN_EVENT));

// Same-tab consent-changed event — see writeConsent() above for the reason.
// Exported so panels/hooks on the same page can subscribe.
export const CONSENT_CHANGED_EVENT = 'mif:cookie-consent-changed';

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [anyModalOpen, setAnyModalOpen] = useState(false);
  const location = useLocation();
  const shouldHide = HIDE_ON_PATHS.some(p => location.pathname.startsWith(p)) || anyModalOpen;

  // On mount: decide visibility AND apply the existing consent so PostHog
  // either loads (accepted) or is firmly stopped (rejected / not yet decided).
  useEffect(() => {
    const consent = readConsent();
    const needsBanner = !consent || consent.version !== CONSENT_VERSION;
    if (needsBanner) {
      // Default-deny: do NOT load PostHog OR Sentry while waiting for user
      // choice. Both trackers stay off until the member clicks Accept All.
      stopPostHog();
      stopSentry();
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    applyConsent(!!consent.accepted);
    return undefined;
  }, []);

  // Listen for "open me again" requests from the Settings page link.
  useEffect(() => {
    const onReopen = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(REOPEN_EVENT, onReopen);
  }, []);

  const handleAccept = useCallback(() => {
    writeConsent(true);
    applyConsent(true);
    setVisible(false);
  }, []);

  const handleReject = useCallback(() => {
    writeConsent(false);
    applyConsent(false);
    setVisible(false);
  }, []);

  if (!visible || shouldHide) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-lg"
      role="dialog"
      aria-label="Cookie preferences"
    >
      <div className="container mx-auto px-4 sm:px-6 py-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
          <div className="flex-1">
            <p className="text-sm text-slate-700">
              We use essential cookies to keep you signed in. With your permission, we also use{' '}
              <span className="font-medium">Sentry</span> (anonymised crash reports) and{' '}
              <span className="font-medium">PostHog</span> (anonymised usage analytics and
              session replay — mouse moves, clicks, navigation) to improve the platform.{' '}
              You can change your choice anytime in <span className="font-medium">Your Privacy &rarr; Cookie preferences</span>.{' '}
              <Link to="/cookie-policy" className="text-emerald-600 hover:underline font-medium">Learn more</Link>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="cookie-reject-btn"
              onClick={handleReject}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
            >
              Reject All
            </button>
            <button
              data-testid="cookie-accept-btn"
              onClick={handleAccept}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;

// Utility for other parts of the app
export const getCookieConsent = () => readConsent();
